import { useQuery } from '@tanstack/react-query';
import { Select } from 'antd';
import { useState, type ReactNode } from 'react';
import type { OfficerDto, Paginated } from '@pams/shared';
import { labelEntriesOf, useLabelCache } from '@/hooks/useLabelCache';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { withChosenOptions } from '@/lib/selectOptions';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C6a §4] THE COMMAND OFFICER PICKER — EXTRACTED FROM `intake/Sections.tsx`, NOT COPIED.
 *
 *  Two hosts now: the builder's by-name command row (`mode: 'multiple'`, unchanged — the same
 *  query, the same key, the same labels) and the fill screen's command SEAT, where a division
 *  names one officer into a position it was asked for (`mode: 'single'`).
 *
 *  ⚠️ THE OFFERED SET IS THE SERVER'S CHECK, BY CONSTRUCTION ([C2b §5]). The seat's naming verb
 *  refuses an officer who is inactive, posted outside the seat's CURRENT addressee, or of a rank
 *  the role does not accept; the single-mode search asks `GET /officers` for exactly those three
 *  — `orgUnitId` + `includeDescendants` (the addressee's subtree, which is `addressedUnitIds`),
 *  `isActive`, and the role's EXPANDED `rankIds`. A picker that offered wider would name somebody
 *  the write then refuses, which is the drift `eligibility.service.ts` forbids.
 *
 *  ⚠️ AND IT CANNOT SEE WHAT THE SERVER STILL REFUSES WITH A 409 — an officer already on another
 *  position of this duty point, or on one of its force posts. Those are facts about OTHER rows, and
 *  the write's sentence is what says so.
 *
 *  ⚠️ NEITHER HOST MAY SHOW A PHONE. `/officers` returns `phone` on every row and the option reads
 *  only the name, rank and unit ([C4a §2b]: matching on a number is not disclosing one).
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ [C2a §4c] The option label is `"<name> · <rank>"`; `withChosenOptions` wants `{ name }`.
 * Splitting on the separator rather than storing a second map keeps ONE remembered string per
 * id — and a label with no separator is returned whole, so a future label shape degrades to
 * "shows the label" rather than to "shows nothing".
 */
const nameFromLabel = (label: string | undefined): { name: string } | undefined =>
  label === undefined ? undefined : { name: label.split(' · ')[0] ?? label };

type OfficerPickerSearch = {
  /** The accessible name — it carries the role and, on the fill screen, the position it fills. */
  label: string;
  disabled?: boolean;
  /** A command role's eligible ranks, EXPANDED ([C2b §5]). Empty means any rank. */
  eligibleRankIds?: readonly string[];
  /** [C6a] Only officers posted at this unit or beneath it — a seat's current addressee. */
  withinOrgUnitId?: string;
  /** [C6a] Only active officers — the seat's naming verb refuses an inactive one. */
  activeOnly?: boolean;
};

export type OfficerPickerProps = OfficerPickerSearch &
  (
    | {
        /** The builder's by-name row: a list of officers held in the form. */
        mode: 'multiple';
        value?: string[];
        onChange?: (value: string[]) => void;
        resolved: ReadonlyMap<string, { name: string }>;
      }
    | {
        /**
         * The fill screen's seat: a CHOOSER, never a holder. The pick is handed to `onPick` and the
         * control goes back to its placeholder — the seat, not this Select, is what holds the name.
         */
        mode: 'single';
        onPick: (officerId: string) => void;
        /**
         * ⚠️ READ NOTHING UNTIL THE LIST IS OPENED. A sheet with several empty seats would otherwise
         * read the officer list once per addressee on every load of a screen that is re-read after
         * every write — for a choice most visits never open.
         */
        loadOnOpen?: boolean;
        placeholder?: string;
        testId?: string;
      }
  );

export const OfficerPicker = (props: OfficerPickerProps) => {
  const { label, disabled, eligibleRankIds, withinOrgUnitId, activeOnly } = props;
  const [search, setSearch] = useState('');
  const [opened, setOpened] = useState(false);
  const trimmed = search.trim();
  const restricted = (eligibleRankIds ?? []).length > 0;
  /*
   * ⚠️ THE BUILDER'S PARAMETERS ARE EXACTLY WHAT THEY WERE — the two [C6a] narrowings are spread
   * in only when given, so its query key (and its cache entry) does not move by one element.
   */
  const params = {
    page: 1,
    limit: 20,
    ...(trimmed ? { search: trimmed } : {}),
    ...(restricted ? { rankIds: (eligibleRankIds ?? []).join(',') } : {}),
    ...(withinOrgUnitId === undefined ? {} : { orgUnitId: withinOrgUnitId, includeDescendants: true }),
    ...(activeOnly ? { isActive: true } : {}),
  };
  const lazy = props.mode === 'single' && props.loadOnOpen === true;

  const officers = useQuery({
    queryKey: queryKeys.officers.list(params),
    queryFn: ({ signal }) => api.get<Paginated<OfficerDto>>('/officers', { query: params, signal }),
    staleTime: 30_000,
    enabled: !lazy || opened,
  });

  const byId = new Map((officers.data?.items ?? []).map((officer) => [officer.id, officer]));
  const found = (officers.data?.items ?? []).map((officer) => ({
    value: officer.id,
    label: `${officer.name} · ${officer.rank?.shortName ?? '—'}`,
  }));

  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [amended C2a §4c] THE OTHER HALF OF THE "No longer available" FIX, ON TAB 2.
   *
   *  `feat/policy` fixed this for `AssociatedUnitsPicker` (tab 1, PA details) and left the
   *  identical defect here (tab 2, Assign duty) — which is where "InCharge" is picked, and
   *  the half the claim's own sentence named.
   *
   *  The mechanism is verbatim tab 1's: `resolved` holds only the officers the SERVER has
   *  already saved on this order, so an officer picked THIS SESSION is absent from it, and
   *  absent from `officers.data` the moment the search term changes. `withChosenOptions` then
   *  reaches its fallback and paints a real, chosen officer as `No longer available`.
   *
   *  ⚠️ THE SESSION CACHE LOSES TO `resolved`, NOT THE OTHER WAY ROUND — spread order below.
   *  The server's copy is authoritative; this only fills the gap the server has not seen yet.
   *
   *  ⚠️ [C6a] A CHOOSER HOLDS NO VALUE, SO IT HAS NO LABEL TO REMEMBER — the hook still runs, in
   *  both modes, because a hook is called on every render or on none.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const { labels: sessionLabels, remember } = useLabelCache();

  const optionRender = (option: { value?: unknown; label?: ReactNode }): ReactNode => {
    const officer = byId.get(String(option.value));
    return (
      <div className="flex flex-col">
        <span>{officer?.name ?? option.label}</span>
        <span className="text-xs text-muted-foreground">
          {[officer?.rank?.shortName, officer?.orgUnit?.name].filter(Boolean).join(' · ')}
        </span>
      </div>
    );
  };
  /* [C4a §2] A phone matches too; the option row still names, and shows no number ([C4a §2b]). */
  const searchPlaceholder = restricted ? 'Search the ranks this role allows' : 'Search by name or phone';

  if (props.mode === 'multiple') {
    const { value, onChange, resolved } = props;
    return (
      <Select
        mode="multiple"
        className="w-full"
        disabled={disabled}
        value={value ?? []}
        onChange={(next: string[], option: unknown) => {
          remember(labelEntriesOf(option));
          onChange?.(next);
        }}
        onSearch={setSearch}
        filterOption={false}
        placeholder={searchPlaceholder}
        aria-label={label}
        loading={officers.isFetching}
        optionRender={optionRender}
        options={withChosenOptions(
          found,
          value ?? [],
          (id) => resolved.get(id) ?? nameFromLabel(sessionLabels.get(id)),
        )}
      />
    );
  }

  const { onPick, placeholder, testId } = props;
  return (
    <Select<string | null>
      showSearch
      className="w-56 max-w-full"
      size="small"
      disabled={disabled}
      /*
       * ⚠️ `null`, NEVER `undefined`. rc-select treats `undefined` as UNCONTROLLED and keeps the pick
       * in its own inner state ([C3c §11b]'s measured `setPending(undefined)` defect), so the chosen
       * name would sit in the box after the write had answered. `null` is controlled AND renders the
       * placeholder — `Select.js`'s own "`null` need show as placeholder instead".
       */
      value={null}
      onChange={(next) => {
        /* The search that found them is spent; the next seat starts from an empty box. */
        setSearch('');
        if (typeof next === 'string') onPick(next);
      }}
      onSearch={setSearch}
      onOpenChange={(open) => {
        if (open) setOpened(true);
      }}
      filterOption={false}
      placeholder={placeholder ?? searchPlaceholder}
      aria-label={label}
      data-testid={testId}
      loading={officers.isFetching}
      notFoundContent={officers.isFetching ? 'Searching…' : 'No officers match'}
      optionRender={optionRender}
      options={found}
    />
  );
};
